import { I18n } from 'react-redux-i18n';
import React from 'react';
import styled from 'styled-components';
import { SectionInfo } from '../../components/SectionInfo';
import { Button } from '../../components/Button';
import {
  SimpleSelect,
  type SimpleSelectOption
} from '../../components/SimpleSelect';
import { MultiChipDropdown } from '../../components/V2ContextBar';
import { ChartCard } from '../../components/ChartCard';
import {
  formatPrice,
  PRICE_COMPARE_OPTIONS,
  PriceCompareMode,
  type PriceByVintageSeries,
  type VintageDistributionSeries
} from '../GlobalView.priceByVintage';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer
} from 'recharts';
import { colors } from '../../theme/tokens';
import {
  EmptyChartState,
  GroupByBtn,
  GroupByWrap,
  PriceChartPoint,
  SortableTh,
  Table,
  Td,
  TdNum,
  VintageYearRow
} from './shared';
import { ChartSkeleton } from './loading';
import type { GlobalViewRow, TableSort } from './types';

export const IndicatorsViewTabs = styled(GroupByWrap)`
  flex: 0 0 auto;
  margin-left: 6px;
  margin-bottom: 0;

  @media (max-width: 760px) {
    flex-basis: 100%;
    width: max-content;
    margin-left: 0;
    margin-top: 8px;
  }
`;

export const IndicatorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
`;

export const VintageKpiSection = styled.div`
  padding: 4px 0 24px;
`;

export const VintageBrandTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;

  table {
    min-width: 1080px;
  }
`;

export const IndicatorCell = styled.div`
  padding: 16px;
  border: 1px solid var(--line-soft, #e8e5df);
  border-radius: 10px;
  background: var(--paper, #f9f7f4);
`;

export const IndicatorLabel = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

export const IndicatorValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: var(--ink);
`;

export const VintageBarChart: React.FC<{
  data: VintageChartRow[];
  valueLabel: string;
  compareLabel?: string | null;
  valueFormatter?: (value: number) => string;
  highlightAll?: boolean;
}> = ({ data, valueLabel, compareLabel, valueFormatter, highlightAll }) => (
  <ResponsiveContainer width="100%" height={300}>
    <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
      <XAxis dataKey="vintage" tick={{ fontSize: 11, fill: colors.inkMuted }} />
      <YAxis
        width={54}
        tick={{ fontSize: 11, fill: colors.inkMuted }}
        tickFormatter={(value: number) =>
          valueFormatter ? valueFormatter(value) : String(value)
        }
      />
      <Tooltip
        content={
          <VintageChartTooltip
            valueLabel={valueLabel}
            compareLabel={compareLabel}
            valueFormatter={valueFormatter}
          />
        }
      />
      {compareLabel && <Legend wrapperStyle={{ fontSize: 12 }} />}
      <Bar
        dataKey="value"
        name={valueLabel}
        fill={colors.terracotta}
        radius={[3, 3, 0, 0]}
      >
        {data.map((point) => (
          <Cell
            key={point.vintage}
            fill={
              highlightAll && point.vintage === I18n.t('placeholderAll')
                ? colors.ink
                : colors.terracotta
            }
          />
        ))}
      </Bar>
      {compareLabel && (
        <Bar
          dataKey="compare"
          name={compareLabel}
          fill={colors.ink}
          radius={[3, 3, 0, 0]}
        />
      )}
    </BarChart>
  </ResponsiveContainer>
);

export interface VintageChartRow {
  vintage: string;
  value: number | null;
  compare?: number | null;
  minPrice?: number | null;
  medianPrice?: number | null;
  maxPrice?: number | null;
  facing?: number | null;
  posCount?: number | null;
}

export const VintageChartTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload?: VintageChartRow }>;
  valueLabel: string;
  compareLabel?: string | null;
  valueFormatter?: (value: number) => string;
}> = ({ active, payload, valueLabel, compareLabel, valueFormatter }) => {
  const point = payload?.[0]?.payload;
  if (!active || !point) return null;
  const format = (value: number | null | undefined) =>
    value == null ? '—' : valueFormatter ? valueFormatter(value) : value;
  const formatCount = (value: number | null | undefined) =>
    value == null ? '—' : Math.round(value).toLocaleString();

  return (
    <VintageTooltipCard>
      <strong>{point.vintage}</strong>
      <span>
        {point.medianPrice != null
          ? valueLabel
          : `${valueLabel}: ${format(point.value)}`}
      </span>
      {compareLabel &&
        point.compare != null &&
        (point.medianPrice != null ? (
          <>
            <span>{compareLabel}</span>
            <span>
              {I18n.t('globalView.price.indicators.medianPrice')}:{' '}
              {format(point.compare)}
            </span>
          </>
        ) : (
          <span>
            {compareLabel}: {format(point.compare)}
          </span>
        ))}
      {point.medianPrice != null && (
        <>
          <VintageTooltipDivider />
          <span>
            {I18n.t('globalView.price.indicators.medianPrice')}:{' '}
            {format(point.medianPrice)}
          </span>
          <span>
            {I18n.t('globalView.price.facing')}: {formatCount(point.facing)}
          </span>
          <span>
            {I18n.t('globalView.price.posCount')}: {formatCount(point.posCount)}
          </span>
        </>
      )}
    </VintageTooltipCard>
  );
};

export const PriceControls = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
`;

export const PriceRangeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--ink-muted);
  font-size: 12px;
  white-space: nowrap;
`;

export const VintageTooltipCard = styled.div`
  display: grid;
  gap: 5px;
  padding: 10px 12px;
  border: 1px solid var(--line);
  border-radius: 6px;
  background: var(--white);
  box-shadow: 0 4px 14px rgb(25 22 17 / 12%);
  color: var(--ink);
  font-size: 12px;
`;

export const VintageTooltipDivider = styled.div`
  height: 1px;
  margin: 2px 0;
  background: var(--line-soft, #e8e5df);
`;

export const PriceDownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--ink);
  color: var(--white);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--terracotta);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const CompareRadios = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 12.5px;
  color: var(--ink-muted);
  label {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
  }
`;

// ─── Sales Tracking: selection bar + drawer (mirrors proto's #selectionBar
// and #gvTrackingDrawer — same mechanism as the main Points of Sale page). ──

export interface VintagePriceTabProps {
  competitors: string[];
  competitorOptions: { value: string; label: string }[];
  exportVintageIndicators: () => void;
  hasPriceSelection: boolean;
  hasVintageSelection: boolean;
  isLoadingVintBrandData: boolean;
  isPriceError: boolean;
  isPriceLoading: boolean;
  isVintageDistributionError: boolean;
  isVintageDistributionLoading: boolean;
  priceChartCompareLabel: string | null;
  priceChartData: PriceChartPoint[];
  priceChartValueLabel: string;
  priceCompare: PriceCompareMode;
  priceCurrency: 'USD' | 'EUR' | 'GBP';
  priceError: unknown;
  priceSeries: PriceByVintageSeries[];
  priceVintageFrom: string;
  priceVintageOptions: SimpleSelectOption[];
  priceVintageTo: string;
  priceWine: string | number | null;
  refetchPriceByVintage: () => void;
  refetchVintageDistribution: () => void;
  renderPercent: (value?: number | null) => React.ReactNode;
  setCompetitors: React.Dispatch<React.SetStateAction<string[]>>;
  setPriceCompare: React.Dispatch<React.SetStateAction<PriceCompareMode>>;
  setPriceVintageFrom: React.Dispatch<React.SetStateAction<string>>;
  setPriceVintageTo: React.Dispatch<React.SetStateAction<string>>;
  setPriceWine: React.Dispatch<React.SetStateAction<string | number | null>>;
  setVintPanel: React.Dispatch<React.SetStateAction<'brand' | 'vintage'>>;
  vintBrandRows: GlobalViewRow[];
  vintBrandSort: TableSort<GlobalViewRow>;
  vintPanel: 'brand' | 'vintage';
  vintageChartCompareLabel: string | null;
  vintageChartData: VintageChartRow[];
  vintageChartValueLabel: string;
  vintageDistributionError: unknown;
  vintageNdKpis: { label: string; value: number }[];
  vintageSeries: VintageDistributionSeries[];
  vintageSort: TableSort<VintageYearRow>;
  vintageYearRows: VintageYearRow[];
  wineOptions: SimpleSelectOption[];
}

export const VintagePriceTab: React.FC<VintagePriceTabProps> = ({
  competitors,
  competitorOptions,
  exportVintageIndicators,
  hasPriceSelection,
  hasVintageSelection,
  isLoadingVintBrandData,
  isPriceError,
  isPriceLoading,
  isVintageDistributionError,
  isVintageDistributionLoading,
  priceChartCompareLabel,
  priceChartData,
  priceChartValueLabel,
  priceCompare,
  priceCurrency,
  priceError,
  priceSeries,
  priceVintageFrom,
  priceVintageOptions,
  priceVintageTo,
  priceWine,
  refetchPriceByVintage,
  refetchVintageDistribution,
  renderPercent,
  setCompetitors,
  setPriceCompare,
  setPriceVintageFrom,
  setPriceVintageTo,
  setPriceWine,
  setVintPanel,
  vintBrandRows,
  vintBrandSort,
  vintPanel,
  vintageChartCompareLabel,
  vintageChartData,
  vintageChartValueLabel,
  vintageDistributionError,
  vintageNdKpis,
  vintageSeries,
  vintageSort,
  vintageYearRows,
  wineOptions
}) => (
  <ChartCard
    titleVariant="section"
    title={
      <>
        {I18n.t('globalView.price.indicatorsTitle')}
        <SectionInfo
          title={I18n.t('globalView.price.indicatorsInfoTitle')}
          body={<p>{I18n.t('globalView.price.indicatorsInfoBody')}</p>}
        />
        <IndicatorsViewTabs
          role="tablist"
          aria-label={I18n.t('globalView.price.indicatorsTitle')}
        >
          <GroupByBtn
            type="button"
            role="tab"
            aria-selected={vintPanel === 'vintage'}
            $active={vintPanel === 'vintage'}
            onClick={() => setVintPanel('vintage')}
          >
            {I18n.t('globalView.price.byVintage')}
          </GroupByBtn>
          <GroupByBtn
            type="button"
            role="tab"
            aria-selected={vintPanel === 'brand'}
            $active={vintPanel === 'brand'}
            onClick={() => setVintPanel('brand')}
          >
            {I18n.t('globalView.price.byProductBrand')}
          </GroupByBtn>
        </IndicatorsViewTabs>
      </>
    }
    meta={
      <PriceDownloadButton
        type="button"
        aria-label={I18n.t('globalView.price.exportCsv')}
        title={I18n.t('globalView.price.exportCsv')}
        disabled={
          vintPanel === 'vintage'
            ? vintageYearRows.length === 0
            : vintBrandRows.length === 0
        }
        onClick={exportVintageIndicators}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      </PriceDownloadButton>
    }
    surface="white"
  >
    {vintPanel === 'vintage' && (
      <>
        <PriceControls>
          <SimpleSelect
            options={wineOptions}
            value={priceWine}
            onChange={setPriceWine}
            placeholder={I18n.t('globalView.price.selectWine')}
            minWidth="220px"
          />
          <PriceRangeControl>
            <span>{I18n.t('globalView.price.fromVintage')}</span>
            <SimpleSelect
              options={priceVintageOptions}
              value={priceVintageFrom}
              onChange={(value) => setPriceVintageFrom(String(value))}
              minWidth="112px"
            />
          </PriceRangeControl>
          <PriceRangeControl>
            <span>{I18n.t('globalView.price.toVintage')}</span>
            <SimpleSelect
              options={priceVintageOptions}
              value={priceVintageTo}
              onChange={(value) => setPriceVintageTo(String(value))}
              minWidth="112px"
            />
          </PriceRangeControl>
          <CompareRadios>
            {PRICE_COMPARE_OPTIONS.map((opt) => (
              <label key={opt.value}>
                <input
                  name="priceCompareMode"
                  type="radio"
                  checked={priceCompare === opt.value}
                  disabled={
                    opt.value === 'competitor' && competitorOptions.length === 0
                  }
                  title={
                    opt.value === 'competitor' && competitorOptions.length === 0
                      ? I18n.t('globalView.price.competitorUnavailable')
                      : undefined
                  }
                  onChange={() => setPriceCompare(opt.value)}
                />
                {I18n.t(opt.translationKey)}
              </label>
            ))}
          </CompareRadios>
          {priceCompare === 'competitor' && (
            <MultiChipDropdown
              options={competitorOptions}
              selected={competitorOptions.filter((option) =>
                competitors.includes(option.value)
              )}
              onChange={(selected) =>
                setCompetitors(selected.map((option) => option.value))
              }
              placeholder={I18n.t('v2GlobalView.contextBar.noCompetitors')}
              allLabel={I18n.t('v2GlobalView.contextBar.noCompetitors')}
              variant="brand"
              neutral
            />
          )}
        </PriceControls>

        {vintageNdKpis.length > 0 && (
          <VintageKpiSection>
            <IndicatorsGrid>
              {vintageNdKpis.map((kpi) => (
                <IndicatorCell key={kpi.label}>
                  <IndicatorLabel>
                    Numerical Distribution, all selected vintages · {kpi.label}
                  </IndicatorLabel>
                  <IndicatorValue>
                    {Math.round(kpi.value).toLocaleString()}
                  </IndicatorValue>
                </IndicatorCell>
              ))}
            </IndicatorsGrid>
          </VintageKpiSection>
        )}

        <ChartCard
          title={I18n.t('globalView.price.title')}
          subtitle={I18n.t('globalView.price.currencySubtitle', {
            currency: priceCurrency
          })}
          variant="section"
          titleVariant="section"
        >
          {!hasPriceSelection ? (
            <EmptyChartState>
              {I18n.t('globalView.price.selectWinePrompt')}
            </EmptyChartState>
          ) : isPriceLoading ? (
            <ChartSkeleton label={I18n.t('globalView.price.loading')} />
          ) : isPriceError ? (
            <EmptyChartState>
              <div>
                {(priceError as unknown as Error | null)?.message ||
                  I18n.t('globalView.price.error')}
                <div>
                  <Button onClick={() => void refetchPriceByVintage()}>
                    {I18n.t('globalView.price.retry')}
                  </Button>
                </div>
              </div>
            </EmptyChartState>
          ) : priceSeries.length === 0 ? (
            <EmptyChartState>
              {I18n.t('globalView.price.empty')}
            </EmptyChartState>
          ) : !priceChartData.slice(1).some((point) => point.value != null) ? (
            <EmptyChartState>
              {I18n.t('globalView.price.noPricedPoints')}
            </EmptyChartState>
          ) : (
            <VintageBarChart
              data={priceChartData}
              valueLabel={priceChartValueLabel}
              compareLabel={priceChartCompareLabel}
              valueFormatter={(value) => formatPrice(value, priceCurrency)}
              highlightAll
            />
          )}
        </ChartCard>

        <ChartCard
          title={I18n.t('v2GlobalView.vintages.chartTitle')}
          subtitle={vintageChartValueLabel}
          variant="section"
          titleVariant="section"
        >
          {!hasVintageSelection ? (
            <EmptyChartState>
              {I18n.t('globalView.price.selectWinePrompt')}
            </EmptyChartState>
          ) : isVintageDistributionLoading ? (
            <ChartSkeleton label={I18n.t('globalView.price.loading')} />
          ) : isVintageDistributionError ? (
            <EmptyChartState>
              <div>
                {(vintageDistributionError as unknown as Error | null)
                  ?.message || I18n.t('globalView.price.error')}
                <div>
                  <Button onClick={() => void refetchVintageDistribution()}>
                    {I18n.t('globalView.price.retry')}
                  </Button>
                </div>
              </div>
            </EmptyChartState>
          ) : vintageSeries.length === 0 ? (
            <EmptyChartState>
              {I18n.t('globalView.price.empty')}
            </EmptyChartState>
          ) : (
            <VintageBarChart
              data={vintageChartData}
              valueLabel={vintageChartValueLabel}
              compareLabel={vintageChartCompareLabel}
              valueFormatter={(value) => Math.round(value).toLocaleString()}
            />
          )}
        </ChartCard>

        <ChartCard
          title={I18n.t('globalView.price.indicatorsByVintage')}
          variant="section"
          titleVariant="section"
        >
          {vintageYearRows.length === 0 ? (
            <EmptyChartState>
              {I18n.t('globalView.price.empty')}
            </EmptyChartState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <SortableTh
                    label={I18n.t('globalView.price.indicators.vintage')}
                    sortKey="vintage"
                    sort={vintageSort.sort}
                    onSort={vintageSort.onSort}
                  />
                  <SortableTh
                    label={I18n.t('globalView.price.indicators.medianPrice')}
                    sortKey="medianPrice"
                    sort={vintageSort.sort}
                    onSort={vintageSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.consumptionRate'
                    )}
                    sortKey="consumptionRate"
                    sort={vintageSort.sort}
                    onSort={vintageSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t('globalView.price.indicators.brandRefs')}
                    sortKey="brandRefs"
                    sort={vintageSort.sort}
                    onSort={vintageSort.onSort}
                    align="right"
                  />
                  {priceCompare === 'competitor' &&
                    competitorOptions.some((o) =>
                      competitors.includes(o.value)
                    ) && (
                      <SortableTh
                        label={I18n.t(
                          'globalView.price.indicators.competitorRefs'
                        )}
                        sortKey="competitorRefs"
                        sort={vintageSort.sort}
                        onSort={vintageSort.onSort}
                        align="right"
                      />
                    )}
                </tr>
              </thead>
              <tbody>
                {vintageSort.sortedRows.map((row) => (
                  <tr key={row.vintage}>
                    <Td>
                      <strong>{row.vintage}</strong>
                    </Td>
                    <TdNum>
                      {row.medianPrice != null
                        ? formatPrice(row.medianPrice, priceCurrency)
                        : '—'}
                    </TdNum>
                    <TdNum>{renderPercent(row.consumptionRate)}</TdNum>
                    <TdNum>{row.brandRefs}</TdNum>
                    {priceCompare === 'competitor' &&
                      competitorOptions.some((o) =>
                        competitors.includes(o.value)
                      ) && <TdNum>{row.competitorRefs}</TdNum>}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ChartCard>
      </>
    )}

    {vintPanel === 'brand' && (
      <ChartCard
        title={null}
        subtitle={I18n.t('globalView.price.allBrandsScopeDescription')}
        variant="section"
        titleVariant="section"
      >
        {isLoadingVintBrandData ? (
          <ChartSkeleton label={I18n.t('globalView.price.loading')} />
        ) : vintBrandRows.length === 0 ? (
          <EmptyChartState>
            {I18n.t('globalView.price.noBrands')}
          </EmptyChartState>
        ) : (
          <VintageBrandTableScroll>
            <Table style={{ minWidth: 1080 }}>
              <thead>
                <tr>
                  <SortableTh
                    label={I18n.t('globalView.price.indicators.productBrand')}
                    sortKey="name"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.avgVintageLastSurvey'
                    )}
                    sortKey="avgVintageLast"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.avgVintagePreviousSurvey'
                    )}
                    sortKey="avgVintagePrevious"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.consumptionRate'
                    )}
                    sortKey="consumption"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t('globalView.price.indicators.noveltyRate')}
                    sortKey="novelty"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.top3NewVintages'
                    )}
                    sortKey="topNewVintages"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.mostPresentVintage'
                    )}
                    sortKey="vintage"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.mostConsumedVintage'
                    )}
                    sortKey="mostConsumed"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                  <SortableTh
                    label={I18n.t(
                      'globalView.price.indicators.ndOlderVintages'
                    )}
                    sortKey="older"
                    sort={vintBrandSort.sort}
                    onSort={vintBrandSort.onSort}
                    align="right"
                  />
                </tr>
              </thead>
              <tbody>
                {vintBrandRows.map((row: any, i: number) => (
                  <tr key={`${row.entity_name}-${i}`}>
                    <Td>
                      <strong>{row.entity_name}</strong>
                    </Td>
                    <TdNum>—</TdNum>
                    <TdNum>—</TdNum>
                    <TdNum>{renderPercent(row.consumption_rate)}</TdNum>
                    <TdNum>—</TdNum>
                    <Td>—</Td>
                    <Td>{row.most_present_vintage || '—'}</Td>
                    <TdNum>—</TdNum>
                    <TdNum>{row.dn_older_vintages ?? '—'}</TdNum>
                  </tr>
                ))}
              </tbody>
            </Table>
          </VintageBrandTableScroll>
        )}
      </ChartCard>
    )}
  </ChartCard>
);
